import {
  createElement,
  type AnchorHTMLAttributes,
  type ComponentType,
  type ImgHTMLAttributes,
} from "react";

export type RavioliImagePrimitiveProps = ImgHTMLAttributes<HTMLImageElement> & {
  alt: string;
  imageClassName?: string;
  priority?: boolean;
  src: string;
};

export type RavioliLinkPrimitiveProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  href: string;
};

export type RavioliPrimitiveRegistry = {
  Image: ComponentType<RavioliImagePrimitiveProps>;
  Link: ComponentType<RavioliLinkPrimitiveProps>;
};

export type RavioliPrimitiveName = keyof RavioliPrimitiveRegistry;

export interface RavioliFormSubmissionField {
  label: string;
  type: "short-text" | "email" | "phone" | "long-text" | "select" | "checkbox";
  value: string | boolean;
}

export interface RavioliFormSubmission {
  fields: RavioliFormSubmissionField[];
  sourcePath: string;
}

export type RavioliFormSubmitter = (submission: RavioliFormSubmission) => Promise<void>;

const Image = ({ imageClassName, priority: _priority, ...props }: RavioliImagePrimitiveProps) =>
  createElement("img", {
    ...props,
    className: [props.className, imageClassName].filter(Boolean).join(" ") || undefined,
  });

const Link = ({ children, rel, target, ...props }: RavioliLinkPrimitiveProps) =>
  createElement(
    "a",
    { ...props, rel: target === "_blank" && !rel ? "noopener noreferrer" : rel, target },
    children,
  );

const registry: RavioliPrimitiveRegistry = { Image, Link };
let formSubmitter: RavioliFormSubmitter | undefined;

export function registerComponent<K extends RavioliPrimitiveName>(
  name: K,
  component: RavioliPrimitiveRegistry[K],
) {
  registry[name] = component;
}

export function getComponent<K extends RavioliPrimitiveName>(name: K): RavioliPrimitiveRegistry[K] {
  return registry[name];
}

export function registerFormSubmitter(submitter: RavioliFormSubmitter) {
  formSubmitter = submitter;
}

export async function submitForm(submission: RavioliFormSubmission) {
  if (!formSubmitter) throw new Error("Form submission is not configured");
  await formSubmitter(submission);
}
